import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import '../styles/Contact.css';
import SEO from '../components/SEO';

// Initialize EmailJS
emailjs.init("JfNJ2uDg8qNeZvqNx"); // Replace with your Public Key

// Update these with your actual EmailJS credentials
const EMAILJS_SERVICE_ID = 'service_vpbd3en';  // Your Service ID
const EMAILJS_TEMPLATE_ID = 'template_i8zo0h7'; // Your Template ID
const EMAILJS_PUBLIC_KEY = 'JfNJ2uDg8qNeZvqNx'; // Your Public Key

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    serviceType: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    details: ''
  });

  const [selectedService, setSelectedService] = useState('');
  const [loading, setLoading] = useState(false);

  const serviceTypes = [
    { id: 'forest-management', label: 'Forest Management' },
    { id: 'tree-removal', label: 'Tree & Stump Removal' },
    { id: 'wellness', label: 'Plant & Tree Wellness' },
    { id: 'pruning', label: 'General Pruning' },
    { id: 'fire-safety', label: 'Fire Safety' },
    { id: 'snow-removal', label: 'Snow Removal' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Create a sanitized version of the form data
    const templateParams = {
      // Contact Info
      firstName: formData.firstName || '',
      lastName: formData.lastName || '',
      email: formData.email || '',
      phone: formData.phone || '',
      address: formData.address || '',
      city: formData.city || '',
      state: formData.state || '',
      zip: formData.zip || '',
      
      // Service Type
      serviceType: formData.serviceType || '',
      
      // Forest Management Fields
      treeTypes: formData.treeTypes || '',
      brushPiles: formData.brushPiles || '',
      floorCleanup: formData.floorCleanup || '',
      lotSize: formData.lotSize || '',
      pineNeedles: formData.pineNeedles || '',
      terrainSlope: formData.terrainSlope || '',
      dangerLevel: formData.dangerLevel || '',
      
      // Tree Removal Fields
      treeType: formData.treeType || '',
      treeSize: formData.treeSize || '',
      obstacles: formData.obstacles || '',
      truckAccess: formData.truckAccess || '',
      
      // Wellness Fields
      desiredPlants: formData.desiredPlants || '',
      treeAmount: formData.treeAmount || '',
      budget: formData.budget || '',
      plantHealth: formData.plantHealth || '',
      damage: formData.damage || '',
      knownIssues: formData.knownIssues || '',
      
      // Pruning Fields
      season: formData.season || '',
      pruningType: formData.pruningType || '',
      plantCount: formData.plantCount || '',
      location: formData.location || '',
      
      // Fire Safety Fields
      protection: formData.protection || '',
      areaHistory: formData.areaHistory || '',
      dryZone: formData.dryZone || '',
      maintenance: formData.maintenance || '',
      
      // Snow Removal Fields
      snowServiceType: formData.serviceType || '', // Renamed to avoid conflict
      snowLotSize: formData.lotSize || '', // Renamed to avoid conflict
      additionalAreas: formData.additionalAreas || '',
      
      // Additional Details
      details: formData.details || ''
    };

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      alert('Message sent successfully!');
      resetForm();
    } catch (error) {
      console.error('EmailJS Error:', error);
      alert('Failed to send message. Please try again.');
    }

    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      serviceType: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      details: ''
    });
    setSelectedService('');
  };

  const handleServiceSelect = (serviceId) => {
    setSelectedService(serviceId);
    setFormData(prev => ({
      ...prev,
      serviceType: serviceId
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // eslint-disable-next-line no-unused-vars
  const isValidEmail = (email) => {
    return /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email);
  };

  // Dynamic form fields based on service type
  const renderServiceSpecificFields = () => {
    switch (selectedService) {
      case 'forest-management':
        return (
          <div className="form-group">
            <label className="label">Forest Management Details</label>
            <select className="select" name="treeTypes" onChange={handleChange} required>
              <option value="">What types of trees?</option>
              <option value="pine">Pine</option>
              <option value="oak">Oak</option>
              <option value="maple">Maple</option>
              <option value="mixed">Mixed</option>
            </select>
            <select className="select" name="brushPiles" onChange={handleChange} required>
              <option value="">Brush piles present?</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            <select className="select" name="floorCleanup" onChange={handleChange} required>
              <option value="">Surface floor cleanup needed?</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            <input
              className="input"
              type="text"
              name="lotSize"
              placeholder="Lot size (acres)"
              onChange={handleChange}
              required
            />
            <select className="select" name="pineNeedles" onChange={handleChange} required>
              <option value="">Pine needle cleanup needed?</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            <select className="select" name="terrainSlope" onChange={handleChange} required>
              <option value="">Terrain slope</option>
              <option value="flat">Flat</option>
              <option value="gentle">Gentle</option>
              <option value="moderate">Moderate</option>
              <option value="steep">Steep</option>
            </select>
            <select className="select" name="dangerLevel" onChange={handleChange} required>
              <option value="">Danger level (1-10)</option>
              {[...Array(10)].map((_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
          </div>
        );

      case 'tree-removal':
        return (
          <div className="form-group">
            <label className="label">Tree & Stump Removal Details</label>
            <input
              className="input"
              type="text"
              name="treeType"
              placeholder="What type of tree needs removal?"
              onChange={handleChange}
              required
            />
            <select className="select" name="treeSize" onChange={handleChange} required>
              <option value="">Estimated tree size</option>
              <option value="small">Small (under 20ft)</option>
              <option value="medium">Medium (20-40ft)</option>
              <option value="large">Large (40-60ft)</option>
              <option value="very-large">Very Large (60ft+)</option>
            </select>
            <textarea
              className="textarea"
              name="obstacles"
              placeholder="Are there any objects in the way? (buildings, power lines, etc.)"
              onChange={handleChange}
              required
            />
            <select className="select" name="truckAccess" onChange={handleChange} required>
              <option value="">Is there easy access for a dump truck?</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
              <option value="unsure">Unsure</option>
            </select>
          </div>
        );

      case 'wellness':
        return (
          <div className="form-group">
            <label className="label">Plant & Tree Wellness Details</label>
            <textarea
              className="textarea"
              name="desiredPlants"
              placeholder="What type of plants/trees do you want?"
              onChange={handleChange}
              required
            />
            <input
              className="input"
              type="number"
              name="treeAmount"
              placeholder="Number of trees/plants"
              onChange={handleChange}
              required
            />
            <select className="select" name="budget" onChange={handleChange} required>
              <option value="">Budget range for new trees/plants</option>
              <option value="0-500">$0-$500</option>
              <option value="501-1000">$501-$1,000</option>
              <option value="1001-2500">$1,001-$2,500</option>
              <option value="2501+">$2,501+</option>
            </select>
            <select className="select" name="plantHealth" onChange={handleChange} required>
              <option value="">Current plant health</option>
              <option value="excellent">Excellent</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="poor">Poor</option>
              <option value="critical">Critical</option>
            </select>
            <textarea
              className="textarea"
              name="damage"
              placeholder="What damaged the tree/plant? (if applicable)"
              onChange={handleChange}
            />
            <textarea
              className="textarea"
              name="knownIssues"
              placeholder="Do you know what's wrong? Please describe."
              onChange={handleChange}
            />
          </div>
        );

      case 'pruning':
        return (
          <div className="form-group">
            <label className="label">Pruning Service Details</label>
            <select className="select" name="season" onChange={handleChange} required>
              <option value="">Preferred season for pruning</option>
              <option value="spring">Spring</option>
              <option value="summer">Summer</option>
              <option value="fall">Fall</option>
              <option value="winter">Winter</option>
            </select>
            <select className="select" name="pruningType" onChange={handleChange} required>
              <option value="">Type of pruning needed</option>
              <option value="full">Full</option>
              <option value="light">Light</option>
              <option value="medium">Medium</option>
            </select>
            <input
              className="input"
              type="number"
              name="plantCount"
              placeholder="Number of plants/trees to be pruned"
              onChange={handleChange}
              required
            />
            <textarea
              className="textarea"
              name="location"
              placeholder="Location details of plants/trees"
              onChange={handleChange}
              required
            />
          </div>
        );

      case 'fire-safety':
        return (
          <div className="form-group">
            <label className="label">Fire Safety Assessment</label>
            <select className="select" name="protection" onChange={handleChange} required>
              <option value="">Current yard protection level (1-10)</option>
              {[...Array(10)].map((_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
            <textarea
              className="textarea"
              name="areaHistory"
              placeholder="History of fire in the area"
              onChange={handleChange}
              required
            />
            <select className="select" name="dryZone" onChange={handleChange} required>
              <option value="">Is your property in a dry zone?</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
              <option value="unsure">Unsure</option>
            </select>
            <select className="select" name="maintenance" onChange={handleChange} required>
              <option value="">Interested in preventative maintenance?</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
              <option value="maybe">Maybe</option>
            </select>
          </div>
        );

      case 'snow-removal':
        return (
          <div className="form-group">
            <label className="label">Snow Removal Service Details</label>
            <select className="select" name="serviceType" onChange={handleChange} required>
              <option value="">Type of snow removal needed</option>
              <option value="driveway">Driveway</option>
              <option value="walkway">Walkway</option>
              <option value="roof">Roof</option>
              <option value="commercial">Commercial Lot</option>
              <option value="all">All Areas</option>
            </select>
            <input
              className="input"
              type="text"
              name="lotSize"
              placeholder="Lot size or area to be cleared"
              onChange={handleChange}
              required
            />
            <textarea
              className="textarea"
              name="additionalAreas"
              placeholder="Any other areas needing snow removal? Please describe."
              onChange={handleChange}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SEO page="contact" />
      <div className="contact-section">
        <div className="form-container">
          <h1 className="title">Contact Us</h1>
          
          {/* New Service Type Buttons */}
          <div className="service-buttons">
            {serviceTypes.map((service) => (
              <button
                key={service.id}
                className={`service-button ${selectedService === service.id ? 'active' : ''}`}
                onClick={() => handleServiceSelect(service.id)}
                type="button"
              >
                {service.label}
              </button>
            ))}
          </div>

          <form className="form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="label">Personal Information</label>
              <input
                className="input"
                type="text"
                name="firstName"
                placeholder="First Name"
                required
                onChange={handleChange}
                value={formData.firstName}
              />
              <input
                className="input"
                type="text"
                name="lastName"
                placeholder="Last Name"
                required
                onChange={handleChange}
                value={formData.lastName}
              />
            </div>

            <div className="form-group">
              <label className="label">Contact Information</label>
              <input
                className="input"
                type="email"
                name="email"
                placeholder="Email"
                required
                onChange={handleChange}
                value={formData.email}
                pattern="^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$"
                title="Please enter a valid email address"
              />
              <input
                className="input"
                type="tel"
                name="phone"
                placeholder="Phone"
                required
                onChange={handleChange}
                value={formData.phone}
              />
              <input
                className="input"
                type="text"
                name="address"
                placeholder="Street Address"
                required
                onChange={handleChange}
                value={formData.address}
              />
              <div className="address-group">
                <input
                  className="input city"
                  type="text"
                  name="city"
                  placeholder="City"
                  required
                  onChange={handleChange}
                  value={formData.city}
                />
                <div className="state-zip-group">
                  <input
                    className="input state"
                    type="text"
                    name="state"
                    placeholder="State"
                    required
                    onChange={handleChange}
                    value={formData.state}
                    maxLength="2"
                  />
                  <input
                    className="input zip"
                    type="text"
                    name="zip"
                    placeholder="ZIP Code"
                    required
                    onChange={handleChange}
                    value={formData.zip}
                    pattern="[0-9]{5}"
                    title="Please enter a valid 5-digit ZIP code"
                  />
                </div>
              </div>
            </div>

            <AnimatePresence mode="wait">
              {selectedService && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  {renderServiceSpecificFields()}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="form-group">
              <label className="label">Additional Details</label>
              <textarea
                className="textarea"
                name="details"
                placeholder="Please provide any additional details about your request..."
                onChange={handleChange}
                value={formData.details}
              />
            </div>

            <button className="submit-button" type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Submit Request'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default Contact; 